import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Drawer,
  Button,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ResultScreen from "./Results";
import { useQuiz, useQuizTimer } from "./hooks";
import { getScoreForAnswer, gradeAssessment } from "../../utils";
import completionHeaderImage from "../../assets/images/study/completion-header.svg";
import badgeIcon from "../../assets/images/study/badge.svg";
import openResultIcon from "../../assets/images/quiz/open-result.svg";
import studentImage from "../../assets/images/quiz/student.svg";
import congratsPopperSvg from "../../assets/images/study/congratsPopper.svg";
import { ButtonYellow } from "../../components/buttons";
import arrowSvg from "../../assets/svgs/arrow.svg";
import { useDiagnosticTest } from "../diagnosticTestV2/hooks";
import { useIRGrade } from "../irGrading/hooks";
import { useCourse } from "../course/hooks";
import { useStudy } from "../study/hooks";
import { getFirstCourseAtLevel } from "../../store/course/courseBaseApi";
import { updateProfile as updateProfileApi } from "../../store/profile/profileBaseAPi";

// textshadow function
const generateTextShadow = (fontSize) => {
  const scale = fontSize / 3;
  return `
        0px ${4 * scale}px ${16 * scale}px rgba(0, 0, 0, 0.11),
        ${-1 * scale}px ${-1 * scale}px 0 white,
        ${1 * scale}px ${-1 * scale}px 0 white,
        ${-1 * scale}px ${1 * scale}px 0 white,
        ${1 * scale}px ${1 * scale}px 0 white,
        ${-2 * scale}px ${-2 * scale}px 0 white,
        ${2 * scale}px ${-2 * scale}px 0 white,
        ${-2 * scale}px ${2 * scale}px 0 white,
        ${2 * scale}px ${2 * scale}px 0 white,
        ${-3 * scale}px ${-3 * scale}px 0 white,
        ${3 * scale}px ${-3 * scale}px 0 white,
        ${-3 * scale}px ${3 * scale}px 0 white,
        ${3 * scale}px ${3 * scale}px 0 white,
        ${-4 * scale}px ${-4 * scale}px 0 white,
        ${4 * scale}px ${-4 * scale}px 0 white,
        ${-4 * scale}px ${4 * scale}px 0 white,
        ${4 * scale}px ${4 * scale}px 0 white
    `;
};

// Define custom styles using tss-react
const useStyles = makeStyles()((theme) => ({
  lowMarkContainer: {
    background:
      "linear-gradient(to top, #ffffff, #fefdfe, #fffbfc, #fff9f8, #fff7f3, #fff4ee, #fff2e8, #fef0e2, #fdecda, #fce8d3, #fbe3cb, #fadfc4)",
    textAlign: "center",
    padding: theme.spacing(1),
  },
  innerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  lowMarkCompletedTxt: {
    fontFamily: "'Carlito', sans-serif",
    color: "#383838",
    fontSize: "1em",
    [theme.breakpoints.up("md")]: {
      fontSize: "2em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "3em",
    },
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  lowMarkQuizName: {
    color: "rgba(255, 199, 0, 1)",
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: theme.spacing(2),
    fontFamily: "'MS Sans Serif'",
    fontSize: "2.5em",
    [theme.breakpoints.up("md")]: {
      fontSize: "3em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "3.5em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "4em",
    },
    textShadow: generateTextShadow(3),
  },
  scoreGrid: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-end",
    },
  },
  resultGrid: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-start",
    },
  },
  scoreContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  lowScore: {
    color: "#C7253E",
    fontWeight: "bold",
    lineHeight: 1,
    fontFamily: '"Quicksand", sans-serif',
    fontSize: "2em",
    [theme.breakpoints.up("md")]: {
      fontSize: "3em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "4em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "5em",
    },
  },
  lowScoreTxt: {
    fontWeight: 700,
    fontSize: "1em",
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.5em",
    },
  },
  actionButton: {
    width: "90px",
    cursor: "pointer",
    transition: "transform 0.1s ease, box-shadow 0.1s ease",
  },
  levelContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  courseAssignedText: {
    fontSize: "1em",
    fontWeight: 600,
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    color: "#383838",
    [theme.breakpoints.up("sm")]: { fontSize: "2em" },
    [theme.breakpoints.up("md")]: { fontSize: "2.5em" },
    [theme.breakpoints.up("lg")]: { fontSize: "3em" },
  },
  levelTxt: {
    fontSize: "1em",
    fontWeight: 600,
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    color: "#383838",
    [theme.breakpoints.up("sm")]: { fontSize: "1em" },
    [theme.breakpoints.up("md")]: { fontSize: "1.5em" },
    [theme.breakpoints.up("lg")]: { fontSize: "2em" },
  },
  level: {
    color: "#C7253E",
    fontSize: "1.5em",
    fontWeight: "bold",
    lineHeight: 1,
    fontFamily: '"Quicksand", sans-serif',
    [theme.breakpoints.up("sm")]: { fontSize: "2.5em" },
    [theme.breakpoints.up("md")]: { fontSize: "3em" },
    [theme.breakpoints.up("lg")]: { fontSize: "3.5em" },
  },
  lowMarkNextlvlGrid: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("lg")]: {
      justifyContent: "flex-end",
    },
  },
  lowMarkNextTxt: {
    fontWeight: 600,
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    color: "#383838",
    fontSize: "1em",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "2em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "2.5em",
    },
  },
  lowMarkContinueGrid: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  lowMarkContinueTxt: {
    fontWeight: 700,
    fontFamily: '"Quicksand", sans-serif',
    fontSize: "1.5em",
    zIndex: 2,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.6em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.8em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "2em",
    },
  },
  congratsContainer: {
    backgroundColor: "#f0f8ff",
    position: "relative",
    textAlign: "center",
  },
  bgContainer: {
    position: "absolute",
    zIndex: 0,
  },
  badgeGrid: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-end",
    },
  },
  congratsTxt: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 0,
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-start",
      alignItems: "flex-start",
      marginTop: theme.spacing(4),
    },
  },
  badgeDiv: {
    width: "20%",
    [theme.breakpoints.up("md")]: {
      width: "40%",
    },
  },
  congratsDiv: {
    width: "90%",
    [theme.breakpoints.up("md")]: {
      width: "70%",
    },
  },
  congratsCompletedTxt: {
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    color: "#383838",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "1em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.8em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "2em",
    },
  },
  congratsQuizName: {
    color: "rgba(255, 199, 0, 1)",
    fontWeight: 700,
    lineHeight: 1,
    fontFamily: '"ms sans serif", "Segoe UI", Tahoma, sans-serif',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.8em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "3.5em",
    },
    textShadow: generateTextShadow(3),
  },
  courseName: {
    color: "#C7253E",
    fontWeight: 700,
    lineHeight: 1,
    fontFamily: '"ms sans serif", "Segoe UI", Tahoma, sans-serif',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.8em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "3.5em",
    },
    textShadow: generateTextShadow(3),
  },
  congratsScore: {
    color: "#32CD32",
    fontWeight: "bold",
    lineHeight: 1,
    fontFamily: '"Quicksand", sans-serif',
    [theme.breakpoints.down("sm")]: {
      fontSize: "2em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "3em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "4em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "5em",
    },
  },
  congratsScoreTxt: {
    fontWeight: 700,
    [theme.breakpoints.down("xl")]: {
      fontSize: "1em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.5em",
    },
  },
  studentImageContainer: {
    position: "absolute",
    bottom: 0,
    left: "-5%",
    display: "none",
    [theme.breakpoints.up("lg")]: {
      display: "block",
    },
    [theme.breakpoints.up("xl")]: {
      left: "8%",
    },
  },
  congratsNextlvlGrid: {
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("lg")]: {
      justifyContent: "flex-end",
    },
  },
  congratsNxtTxt: {
    fontSize: "1em",
    fontWeight: 600,
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    color: "#383838",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.6em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.8em",
    },
  },
  congratsContinueGrid: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  congratsContinueTxt: {
    fontWeight: 700,
    fontFamily: '"Quicksand", sans-serif',
    fontSize: "1em",
    zIndex: 2,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.4em",
    },
  },
  tooltipBox: {
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1),
    borderRadius: "20px",
    border: "2px solid #C9B3F8",
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: 12,
    height: "10px",
    position: "relative",
    zIndex: 10,
  },
  arrow: {
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderBottom: "10px solid #C9B3F8",
    position: "absolute",
    left: "-5%",
    top: "30%",
    transform: "translateY(-50%)",
    rotate: "-90deg",
    zIndex: 8,
  },
}));

const QuizComplete = ({
  onComplete,
  mandatePassing,
  onFail,
  mandateSpeechEvaluation,
  onConfettiClose,
  setAssessmentBegin,
  type,
  setAssessmentEnded,
  setAssessmentScreen,
}) => {
  const { classes } = useStyles();
  const {
    overallPercentage,
    quizDetails,
    speechEvaluationScore,
    setOverallPercentage,
    result,
    setGradingResult,
    gradingResult,
    selectedDiagnosticTestMapping: mapping,
    evaluationResult,
  } = useQuiz();
  const { loadCurrent, current, loading } = useCourse();
  const { loadCourseMappingV2, courseMappingListV2 } = useIRGrade();
  const { profile } = useStudy();
  const { endTime, endedAt } = useQuizTimer();
  const { readingScore } = useDiagnosticTest();
  const [open, setOpen] = React.useState(false);
  const [complete, setComplete] = React.useState(false);
  const gradingStatus = React.useRef({ grade: null, passed: null });
  const { nextLevelAssessments } = mapping;
  const [irLevel, setIrLevel] = useState();
  const [courseId, setCourseId] = useState();
  const [isFailed, setIsFailed] = useState(false);
  // True when the backend returns noCourseForStudentType for the resolved
  // irLevel — admin hasn't authored a course of the student's URBAN/RURAL
  // type at that level yet. We show a "no course available" message instead
  // of crossing types.
  const [noCourseForType, setNoCourseForType] = useState(false);

  const level = useMemo(() => {
    if (!nextLevelAssessments) return null;

    if (nextLevelAssessments && nextLevelAssessments.length) {
      return nextLevelAssessments?.find(
        (e) =>
          Number(overallPercentage) >= Number(e.minPercentage) &&
          Number(overallPercentage) <= Number(e.maxPercentage),
      );
    }

    return null;
  }, [overallPercentage]);

  useEffect(() => {
    loadCourseMappingV2();
  }, []);
  const hasNextAssessment = React.useMemo(() => {
    let { grade } = gradingResult || {};
    if (grade) {
      let nextLevel = mapping?.nextLevelAssessments?.find(
        (e) => e.gradeName == grade,
      );
      if (nextLevel && nextLevel.irLevel) return false;
    }

    return Boolean(
      mapping?.nextLevelAssessments && mapping.nextLevelAssessments.length,
    );
  }, [mapping, gradingResult]);

  const obtainedScore = evaluationResult?.marksSecured || 0;

  gradingStatus.current = gradeAssessment(
    quizDetails.gradingModel,
    obtainedScore,
    quizDetails.passGrade,
  );

  const toggleDrawer = (open) => () => {
    setOpen(open);
  };

  const handleComplete = () => {
    setComplete(true);
    if (type === "diagnostic-test-v2") {
      setAssessmentBegin(false);
      setAssessmentEnded(false);
      setAssessmentScreen(false);
    }

    if (onComplete && typeof onComplete === "function") {
      onComplete();
    }

    if (onConfettiClose && typeof onConfettiClose === "function") {
      onConfettiClose();
    }
  };

  React.useEffect(() => {
    let result = gradingStatus.current;
    let percentage = Math.round(
      (obtainedScore / evaluationResult.totalMarks) * 100,
    );
    let _overallScore =
      percentage + (mandateSpeechEvaluation ? readingScore : 0);
    let _overallPercentage = Math.round(
      (_overallScore / (mandateSpeechEvaluation ? 200 : 100)) * 100,
    );

    const gradingDetails = quizDetails.gradingModel?.details || [];
    let gradeRank = {};
    [...gradingDetails]
      .sort((a, b) => b.min - a.min) // Sort grades by the minimum score descending
      .forEach((detail, index) => {
        gradeRank[detail.gradeName] = gradingDetails.length - index; // Higher rank for better grades
      });

    const passGradeRank = gradeRank[quizDetails.passGrade] || 0;

    // Find the grade based on obtainedScore
    let isFailed = true;
    const grade = gradingDetails.find(
      (detail) => obtainedScore >= detail.min && obtainedScore <= detail.max,
    );

    if (grade && gradeRank[grade.gradeName] >= passGradeRank) {
      isFailed = false;
    }

    result.timeTaken = endTime;
    result.scored = obtainedScore;
    result.total = evaluationResult.totalMarks;
    result.endedAt = endedAt;
    result.percentage = percentage;

    setGradingResult(result);
    setOverallPercentage(_overallPercentage);
    setIsFailed(isFailed);
  }, [obtainedScore]);

  function getIrLevelAndCourseId(overallPercentage, nextLevelAssessments) {
    if (!nextLevelAssessments || nextLevelAssessments.length === 0) {
      return null;
    }

    const matchedLevel = nextLevelAssessments.find((assessment) => {
      const min = parseFloat(assessment.minPercentage);
      const max = parseFloat(assessment.maxPercentage);
      return overallPercentage >= min && overallPercentage <= max;
    });

    return {
      irLevel: matchedLevel?.irLevel || null,
      courseId: matchedLevel?.courseId || null,
    };
  }

  useEffect(() => {
    if (nextLevelAssessments) {
      const { irLevel, courseId } = getIrLevelAndCourseId(
        overallPercentage,
        nextLevelAssessments,
      );

      if (irLevel) {
        setIsFailed(true);
        setIrLevel(irLevel);
      } else if (courseId) {
        setIsFailed(false);
        setCourseId(courseId);
      }
    }
  }, [overallPercentage, nextLevelAssessments]);

  useEffect(() => {
    if (!irLevel || !profile?.id) return;

    // Backend filters by the student's geographical_type and applies the
    // no-cross-type policy. If it returns `noCourseForStudentType=true` we
    // surface a clear "no course available" message instead of falling back
    // to the other type's course.
    let cancelled = false;
    (async () => {
      console.log("[Diagnostic→Course] resolving first course", {
        studentId: profile.id,
        studentName: profile.userName || profile.firstName,
        geographicalType: profile.geographicalType,
        targetIrLevel: irLevel,
        overallPercentage,
      });
      try {
        setNoCourseForType(false);
        const { data } = await getFirstCourseAtLevel({
          studentId: profile.id,
          level: irLevel,
        });
        if (cancelled) return;
        console.log("[Diagnostic→Course] backend response", {
          level: irLevel,
          response: data,
          noCourseForStudentType: data?.noCourseForStudentType,
          assignedCourseId: data?.nextLevel?.irCourseId,
          assignedLevel: data?.nextLevel?.level,
        });
        if (data?.noCourseForStudentType) {
          console.warn(
            "[Diagnostic→Course] NO COURSE for this student type at level",
            irLevel,
            "— admin must author a",
            (profile.geographicalType || "URBAN").toUpperCase(),
            "course at this level in /course-mapping.",
          );
          // The user never hits the "Continue" button in this branch (we
          // render the no-course screen and there's nothing to click), so we
          // must save gradeLevel HERE — otherwise it stays "" in the DB and
          // the diagnostic re-runs on the next login. Once admin authors the
          // missing course, Study.jsx's retry-on-load will auto-assign it.
          try {
            const resp = await updateProfileApi({
              body: { gradeLevel: irLevel },
              role: "students",
              id: profile.id,
            });
            console.log(
              "[Diagnostic→Course] PUT /profile/ir saved gradeLevel ->",
              irLevel,
              "response:",
              resp,
            );
          } catch (err) {
            console.error(
              "[Diagnostic→Course] PUT /profile/ir failed while saving gradeLevel ->",
              irLevel,
              err,
            );
          }
          setNoCourseForType(true);
          return;
        }
        const firstCourseId = data?.nextLevel?.irCourseId;
        if (firstCourseId) {
          console.log(
            "[Diagnostic→Course] ✅ assigning courseId",
            firstCourseId,
            "to student",
            profile.id,
          );
          loadCurrent({ id: firstCourseId });
        } else {
          console.warn(
            "[Diagnostic→Course] backend returned no courseId and no flag — unexpected",
            data,
          );
        }
      } catch (e) {
        if (!cancelled) {
          console.error(
            "[Diagnostic→Course] ❌ Failed to resolve first course at level",
            irLevel,
            e,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [irLevel, profile?.id]);

  function LowMarksScreen() {
    return (
      <React.Fragment>
        <Box className={classes.lowMarkContainer}>
          <Box className={classes.innerContainer}>
            <Typography className={classes.lowMarkCompletedTxt} variant="h6">
              You have completed the Assessment for
            </Typography>
            <Typography className={classes.lowMarkQuizName} variant="h4">
              {quizDetails?.name}
            </Typography>
          </Box>
          <Grid container mt={2} mb={1} alignItems={"center"}>
            <Grid item xs={12} md={7} className={classes.scoreGrid}>
              <Box className={classes.scoreContainer}>
                <Typography className={classes.lowScore}>
                  {overallPercentage}%
                </Typography>
                <Typography className={classes.lowScoreTxt} variant="body1">
                  (Overall Percentage)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} className={classes.resultGrid}>
              <Button
                className={classes.actionButton}
                onClick={toggleDrawer(true)}
              >
                <img width={"90%"} src={openResultIcon} alt="open results" />
              </Button>
              <Box className={classes.tooltipBox}>
                View Results
                <Box className={classes.arrow}></Box>
              </Box>
            </Grid>
          </Grid>
          {irLevel && type === "diagnostic-test-v2" && (
            <Box className={classes.levelContainer}>
              <Typography className={classes.courseAssignedText} variant="h4">
                {/* Level Assigned */}
                Course assigned
              </Typography>
              <Typography className={classes.courseName}>
                {loading ? (
                  <CircularProgress sx={{ zIndex: 1000 }} />
                ) : (
                  current && current?.courseName
                )}
              </Typography>
              <Typography className={classes.levelTxt} variant="h5">
                {/* Level Assigned */}
                Level assigned: {level.irLevel}
              </Typography>
            </Box>
          )}
          <Grid container mt={2} alignItems={"center"}>
            <Grid
              item
              xs={12}
              md={12}
              lg={8}
              className={classes.lowMarkNextlvlGrid}
            >
              <Typography className={classes.lowMarkNextTxt} variant="h4">
                {type === "diagnostic-test-v2"
                  ? "Proceed to the Assigned Level"
                  : "Proceed to the next Chapter"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              mb={2}
              md={12}
              lg={4}
              className={classes.lowMarkContinueGrid}
            >
              <ButtonYellow onClick={handleComplete}>
                <Typography className={classes.lowMarkContinueTxt}>
                  Continue
                </Typography>
                <img
                  style={{
                    width: "15%",
                    maxWidth: "100%",
                    marginTop: "5px",
                    height: "auto",
                    zIndex: 2,
                  }}
                  src={arrowSvg}
                  alt="arrow"
                />
              </ButtonYellow>
            </Grid>
          </Grid>
          <Drawer
            anchor="right"
            open={open}
            onClose={toggleDrawer(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: "60%",
                height: "100%", // Full height
                boxSizing: "border-box",
              },
            }}
          >
            <ResultScreen
              onComplete={onComplete}
              onFail={onFail}
              mandatePassing={mandatePassing}
              onNavigateBack={toggleDrawer(false)}
            />
          </Drawer>
        </Box>
      </React.Fragment>
    );
  }

  function CongratulationScreen() {
    return (
      <React.Fragment>
        <Box className={classes.congratsContainer}>
          <Box className={classes.bgContainer}>
            <img style={{ width: "100%" }} src={congratsPopperSvg} alt="" />
          </Box>
          <Grid container mt={1} position={"relative"} spacing={2} zIndex={1}>
            <Grid item xs={12} md={3} className={classes.badgeGrid}>
              <Box className={classes.badgeDiv}>
                <img width={"100%"} src={badgeIcon} alt="" />
              </Box>
            </Grid>
            <Grid item xs={12} md={9} className={classes.congratsTxt}>
              <Box className={classes.congratsDiv}>
                <img width={"100%"} src={completionHeaderImage} alt="" />
              </Box>
            </Grid>
          </Grid>
          <Box className={classes.innerContainer}>
            <Typography className={classes.congratsCompletedTxt} variant="h6">
              You have completed the Assessment for
            </Typography>
            <Typography className={classes.congratsQuizName} variant="h4">
              {quizDetails?.name}
            </Typography>
          </Box>
          <Grid container mt={1} alignItems={"center"}>
            <Grid item xs={12} md={7} className={classes.scoreGrid}>
              <Box className={classes.scoreContainer}>
                <Typography className={classes.congratsScore}>
                  {overallPercentage}%
                </Typography>
                <Typography
                  className={classes.congratsScoreTxt}
                  variant="body1"
                >
                  (Overall Percentage)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} className={classes.resultGrid}>
              <Button
                className={classes.actionButton}
                onClick={toggleDrawer(true)}
              >
                <img width={"100%"} src={openResultIcon} alt="Open Result" />
              </Button>
              <Box className={classes.tooltipBox}>
                View Results
                <Box className={classes.arrow}></Box>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ position: "relative" }}>
            {type === "diagnostic-test-v2" && (
              <Box className={classes.studentImageContainer}>
                <img style={{ width: "40%" }} src={studentImage} alt="" />
              </Box>
            )}
            <Grid container p={1} alignItems={"center"}>
              <Grid
                item
                xs={12}
                md={12}
                lg={8}
                className={classes.congratsNextlvlGrid}
              >
                <Typography className={classes.congratsNxtTxt} variant="h4">
                  {type === "diagnostic-test-v2"
                    ? "Proceed to the next Diagnostic Test"
                    : "Proceed to the next Chapter"}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                mb={2}
                md={12}
                lg={4}
                className={classes.congratsContinueGrid}
              >
                <ButtonYellow onClick={handleComplete}>
                  <Typography className={classes.congratsContinueTxt}>
                    Continue
                  </Typography>
                  <img
                    style={{
                      width: "15%",
                      maxWidth: "100%",
                      marginTop: "5px",
                      height: "auto",
                      zIndex: 2,
                    }}
                    src={arrowSvg}
                    alt=""
                  />
                </ButtonYellow>
              </Grid>
            </Grid>
          </Box>
          <Drawer
            anchor="right"
            open={open}
            onClose={toggleDrawer(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: "60%",
                height: "100%", // Full height
                boxSizing: "border-box",
              },
            }}
          >
            <ResultScreen
              onComplete={onComplete}
              onFail={onFail}
              mandatePassing={mandatePassing}
              onNavigateBack={toggleDrawer(false)}
            />
          </Drawer>
        </Box>
      </React.Fragment>
    );
  }

  // When admin hasn't authored a course of the student's URBAN/RURAL type at
  // the resolved irLevel, show a clear message + Continue button. Continue
  // reloads the page, which re-runs Study.jsx's /level-course retry. If admin
  // has since authored the course, the student is auto-assigned; otherwise
  // they see the "course being prepared" screen.
  if (noCourseForType) {
    return (
      <Box className={classes.lowMarkContainer}>
        <Box className={classes.innerContainer} sx={{ p: 4 }}>
          <Typography className={classes.lowMarkCompletedTxt} variant="h6">
            No course available for this level yet
          </Typography>
          <Typography
            sx={{ mt: 2, mb: 3, maxWidth: 520 }}
            variant="body1"
            color="text.secondary"
            align="center"
          >
            Your next course (level {irLevel}) is still being prepared. Please
            check back soon.
          </Typography>
          <ButtonYellow onClick={() => window.location?.reload()}>
            <Typography
              sx={{
                fontWeight: "700",
                fontFamily: '"Quicksand", sans-serif',
                fontSize: "0.9em",
                zIndex: 2,
              }}
            >
              Continue
            </Typography>
            <img
              style={{
                width: "14px",
                marginLeft: "8px",
                height: "auto",
                zIndex: 2,
              }}
              src={arrowSvg}
              alt=""
            />
          </ButtonYellow>
        </Box>
      </Box>
    );
  }

  return <Box>{isFailed ? <LowMarksScreen /> : <CongratulationScreen />}</Box>;
};

export default QuizComplete;
